import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Building2, UserPlus, ArrowRight, Loader2, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public page where prospects + employees request access.
 * `?type=org` → new-organization signup
 * `?type=employee` → employee w/ access code
 */
export default function RequestAccessPage() {
  const [params] = useSearchParams();
  const initialType = params.get('type') === 'employee' ? 'employee' : params.get('type') === 'org' ? 'org' : null;
  const [mode, setMode] = useState(initialType); // null | 'org' | 'employee'

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FDFA] via-white to-[#E0F2FE] flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <Link to="/" className="inline-flex items-center text-[#0E7490] hover:underline mb-6 text-sm" data-testid="back-to-home">
          ← Back to home
        </Link>

        <Card className="overflow-hidden shadow-lg border-[#E2E8F0]">
          <div className="bg-[#0A2342] p-8 text-white">
            <h1 className="text-3xl font-bold tracking-tight">Request Access to Savenes</h1>
            <p className="text-[#B6E0F0] mt-2 text-sm">
              HIPAA-compliant healthcare intelligence — let's get you set up.
            </p>
          </div>

          <div className="p-8">
            {!mode && <ModePicker onPick={setMode} />}
            {mode === 'org' && <OrgRequestForm onBack={() => setMode(null)} />}
            {mode === 'employee' && <EmployeeRequestForm onBack={() => setMode(null)} />}
          </div>
        </Card>

        <p className="text-center text-xs text-[#94A3B8] mt-6">
          Already have an account? <Link to="/login" className="text-[#0E7490] hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function ModePicker({ onPick }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#64748B] mb-6">Choose what brings you here:</p>
      <button
        onClick={() => onPick('org')}
        className="w-full text-left p-6 border-2 border-[#E2E8F0] rounded-xl hover:border-[#0E7490] hover:bg-[#F0FDFA] transition-all group"
        data-testid="pick-new-org"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#0E7490]/10 flex items-center justify-center shrink-0 group-hover:bg-[#0E7490]/20 transition-colors">
            <Building2 className="w-6 h-6 text-[#0E7490]" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0A2342]">I'm starting a new organization</h3>
            <p className="text-sm text-[#64748B] mt-1">
              Get a free 14-day Starter trial. Our team will review your request and provision your workspace.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#0E7490] group-hover:translate-x-1 transition-all" />
        </div>
      </button>

      <button
        onClick={() => onPick('employee')}
        className="w-full text-left p-6 border-2 border-[#E2E8F0] rounded-xl hover:border-[#0E7490] hover:bg-[#F0FDFA] transition-all group"
        data-testid="pick-employee"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#0E7490]/10 flex items-center justify-center shrink-0 group-hover:bg-[#0E7490]/20 transition-colors">
            <UserPlus className="w-6 h-6 text-[#0E7490]" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0A2342]">I'm joining an existing organization</h3>
            <p className="text-sm text-[#64748B] mt-1">
              Ask your administrator for an access code (looks like <code className="text-xs bg-[#F8FAFC] px-1.5 py-0.5 rounded">SVN-A4F2-9X7Q</code>) and we'll route your request to them.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#0E7490] group-hover:translate-x-1 transition-all" />
        </div>
      </button>
    </div>
  );
}

function OrgRequestForm({ onBack }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '',
    organization_name: '', organization_size: '', role_at_org: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/public/access-requests/organization`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="org-request-success">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/10 flex items-center justify-center">
          <Check className="w-8 h-8 text-[#10B981]" />
        </div>
        <h3 className="text-xl font-semibold text-[#0A2342]">Request received</h3>
        <p className="text-[#64748B] mt-2 max-w-md mx-auto">
          We'll review your request and email <strong>{form.contact_email}</strong> with your Starter trial credentials within one business day.
        </p>
        <Button onClick={() => navigate('/')} className="mt-6 bg-[#0E7490] hover:bg-[#0E7490]/90" data-testid="success-back-home">
          Back to home
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="org-request-form">
      <button type="button" onClick={onBack} className="text-sm text-[#0E7490] hover:underline mb-2">← Choose a different option</button>
      <h2 className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
        <Building2 className="w-5 h-5 text-[#0E7490]" /> Start a new organization
      </h2>
      <p className="text-sm text-[#64748B] -mt-2">14-day Starter trial. No credit card required.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-xs font-medium text-[#0A2342]">Your name *</Label>
          <Input id="name" required value={form.contact_name} onChange={e => set('contact_name', e.target.value)} data-testid="org-contact-name" />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-medium text-[#0A2342]">Work email *</Label>
          <Input id="email" type="email" required value={form.contact_email} onChange={e => set('contact_email', e.target.value)} data-testid="org-contact-email" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs font-medium text-[#0A2342]">Phone</Label>
          <Input id="phone" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} data-testid="org-contact-phone" />
        </div>
        <div>
          <Label htmlFor="role" className="text-xs font-medium text-[#0A2342]">Your role</Label>
          <Input id="role" placeholder="e.g. CEO, Director of Nursing" value={form.role_at_org} onChange={e => set('role_at_org', e.target.value)} data-testid="org-role" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="org" className="text-xs font-medium text-[#0A2342]">Organization name *</Label>
          <Input id="org" required value={form.organization_name} onChange={e => set('organization_name', e.target.value)} data-testid="org-name" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="size" className="text-xs font-medium text-[#0A2342]">Approximate team size</Label>
          <select id="size" className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white" value={form.organization_size} onChange={e => set('organization_size', e.target.value)} data-testid="org-size">
            <option value="">Select…</option>
            <option value="1-10">1–10 employees</option>
            <option value="11-50">11–50 employees</option>
            <option value="51-200">51–200 employees</option>
            <option value="201-500">201–500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="msg" className="text-xs font-medium text-[#0A2342]">Anything else? (optional)</Label>
          <textarea id="msg" rows={3} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" value={form.message} onChange={e => set('message', e.target.value)} data-testid="org-message" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onBack} data-testid="org-cancel">Cancel</Button>
        <Button type="submit" disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="org-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Submit request
        </Button>
      </div>
    </form>
  );
}

function EmployeeRequestForm({ onBack }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [orgPreview, setOrgPreview] = useState(null); // {organization_name, organization_id} | null
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', requested_role: 'nurse', message: '' });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setValidating(true);
    setValidateError('');
    setOrgPreview(null);
    try {
      const r = await axios.get(`${API_URL}/api/public/access-codes/${encodeURIComponent(c)}/validate`);
      setOrgPreview(r.data);
    } catch (err) {
      setValidateError(err.response?.data?.detail || 'Could not validate code');
    } finally { setValidating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!orgPreview) { toast.error('Please verify your access code first'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/public/access-requests/employee`, {
        access_code: code.trim().toUpperCase(),
        ...form,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="employee-request-success">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/10 flex items-center justify-center">
          <Check className="w-8 h-8 text-[#10B981]" />
        </div>
        <h3 className="text-xl font-semibold text-[#0A2342]">Request sent to {orgPreview?.organization_name}</h3>
        <p className="text-[#64748B] mt-2 max-w-md mx-auto">
          Your administrator will review and send your sign-in details to <strong>{form.email}</strong>.
        </p>
        <Button onClick={() => navigate('/')} className="mt-6 bg-[#0E7490] hover:bg-[#0E7490]/90">Back to home</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="employee-request-form">
      <button type="button" onClick={onBack} className="text-sm text-[#0E7490] hover:underline mb-2">← Choose a different option</button>
      <h2 className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
        <UserPlus className="w-5 h-5 text-[#0E7490]" /> Join your organization
      </h2>

      <div>
        <Label htmlFor="code" className="text-xs font-medium text-[#0A2342]">Access code *</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="code"
            placeholder="SVN-XXXX-XXXX"
            value={code}
            onChange={e => { setCode(e.target.value.toUpperCase()); setOrgPreview(null); setValidateError(''); }}
            className="font-mono uppercase tracking-wider"
            data-testid="employee-code-input"
          />
          <Button type="button" onClick={validateCode} disabled={!code.trim() || validating} variant="outline" data-testid="employee-validate-code">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
          </Button>
        </div>
        {orgPreview && (
          <div className="mt-2 px-3 py-2 rounded-md bg-[#10B981]/10 border border-[#10B981]/30 text-sm text-[#065F46] flex items-center gap-2" data-testid="employee-org-preview">
            <Check className="w-4 h-4 text-[#10B981]" />
            Joining: <strong>{orgPreview.organization_name}</strong>
            {orgPreview.label && <span className="text-xs text-[#64748B]">({orgPreview.label})</span>}
          </div>
        )}
        {validateError && (
          <div className="mt-2 px-3 py-2 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#991B1B] flex items-center gap-2" data-testid="employee-code-error">
            <X className="w-4 h-4 text-[#EF4444]" />
            {validateError}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <Label htmlFor="ename" className="text-xs font-medium text-[#0A2342]">Full name *</Label>
          <Input id="ename" required value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="employee-name" />
        </div>
        <div>
          <Label htmlFor="eemail" className="text-xs font-medium text-[#0A2342]">Work email *</Label>
          <Input id="eemail" type="email" required value={form.email} onChange={e => set('email', e.target.value)} data-testid="employee-email" />
        </div>
        <div>
          <Label htmlFor="ephone" className="text-xs font-medium text-[#0A2342]">Phone</Label>
          <Input id="ephone" value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="employee-phone" />
        </div>
        <div>
          <Label htmlFor="erole" className="text-xs font-medium text-[#0A2342]">Requested role *</Label>
          <select id="erole" className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white" value={form.requested_role} onChange={e => set('requested_role', e.target.value)} data-testid="employee-role">
            <option value="nurse">Nurse</option>
            <option value="aide">Aide / HHA</option>
            <option value="physician">Physician</option>
            <option value="care_coordinator">Care Coordinator</option>
            <option value="billing_specialist">Billing</option>
            <option value="practice_admin">Practice Admin</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="emsg" className="text-xs font-medium text-[#0A2342]">Anything else? (optional)</Label>
          <textarea id="emsg" rows={2} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" value={form.message} onChange={e => set('message', e.target.value)} data-testid="employee-message" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onBack}>Cancel</Button>
        <Button type="submit" disabled={busy || !orgPreview} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="employee-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Submit request
        </Button>
      </div>
    </form>
  );
}
